import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DateAdapter } from '@angular/material/core';
import { AvailableLanguages, allEmployeeRoles, getLocaleForLang, patterns } from '@constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { environment } from '@environment';
import {
  ConsumableCountry,
  Language,
  MfaChallengeAction,
  Office,
  ROLES_KEYS,
  UpdateMyselfPayload,
  User,
} from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { OfficeFacade } from '@state/office';
import { UserAuthFacade } from '@state/user-auth';
import { TranslateService } from '@ngx-translate/core';
import { MfaActionVerificationModalService } from '@shared/mfa-action-verification-modal/mfa-action-verification-modal.service';
import { SoftDeleteAccountModalService } from '../modals/soft-delete-account-modal/soft-delete-account-modal.service';
import { RouterFacade } from '@state/router';
import { EnableMfaModalService } from '../modals/enable-mfa-modal/enable-mfa-modal.service';

@Component({
  selector: 'app-user-settings-details',
  templateUrl: './user-settings-details.component.html',
  styleUrls: ['./user-settings-details.component.scss'],
  standalone: false,
})
export class UserSettingsDetailsComponent implements OnInit, OnDestroy {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly fb = inject(FormBuilder);
  private readonly dateAdapter = inject(DateAdapter<Date>);
  private readonly ls = inject(LocalStorageService);
  private readonly officeFacade = inject(OfficeFacade);
  private readonly translateService = inject(TranslateService);
  private readonly deleteAccountModalService = inject(SoftDeleteAccountModalService);
  private readonly routerFacade = inject(RouterFacade);
  private readonly enableMfaModalService = inject(EnableMfaModalService);
  private readonly mfaActionVerificationModalService = inject(MfaActionVerificationModalService);

  private destroy$: Subject<boolean> = new ReplaySubject(1);
  private isPhoneValidationReady = false;

  @Input() myself: User;
  @Input() currentLang: AvailableLanguages;

  public loading$: Observable<boolean> = this.userAuthFacade.loading$;
  public countries$: Observable<ConsumableCountry[]> = this.commonsFacade.getCountries$();
  public languages$: Observable<Language[]> = this.commonsFacade.availableLanguages$;
  public offices$: Observable<Office[]> = this.officeFacade.offices$;
  public updateSuccess$: Observable<{ updatedUser: User }> = this.userAuthFacade.userUpdateSuccess$;
  public shouldShowOfficeField = false;
  public uncacheParam = Date.now();

  // @TODO add proper typing to FormGroup
  public form: FormGroup<any> = this.fb.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    officeId: [null],
    languageId: ['', Validators.compose([Validators.required])],
    name: ['', Validators.compose([Validators.maxLength(200), Validators.pattern(patterns.PLNameValidator)])],
    telephoneNumber: ['', Validators.compose([Validators.required])],
  });

  public API_URL = environment.API_URL;
  public ROLES_KEYS = ROLES_KEYS;
  public userName: string = null;
  public mfaButtonLabelKey = 'ENABLE_MFA_MODAL.ENABLE_ACTION';
  public isMfaButtonDisabled = true;
  public isEnableMfaAction = true;

  public ngOnInit(): void {
    if (!this.currentLang) {
      return;
    }

    this.languages$.pipe(take(1)).subscribe(langs => {
      const langId = langs.find(({ key }) => key === this.currentLang)?.id;
      if (langId) {
        this.form.get('languageId').setValue(langId);
      }
    });

    this.form.patchValue({
      email: this.myself?.email,
      languageId: this.myself?.languageId,
      companyId: this.myself?.companyId,
      name: this.myself?.name,
      telephoneNumber: this.myself?.telephoneNumber,
      officeId: this.myself?.office?.id || null,
    });

    this.updateMfaButtonState();

    this.userName = this.myself.name;
    const myRoleKey = this.myself?.role.key;
    if (myRoleKey !== ROLES_KEYS.Admin) {
      this.form.get('email')?.disable();
      this.form.get('officeId')?.disable();
    }

    // show office field for these roles (neverming if disabled)
    this.shouldShowOfficeField = allEmployeeRoles.includes(this.myself?.role.key as ROLES_KEYS);
    if (this.shouldShowOfficeField) {
      this.officeFacade.getOfficesSimpleList();
    }

    this.countries$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => {
        const telephoneNumberControl = this.form.get('telephoneNumber');

        if (telephoneNumberControl?.invalid) {
          telephoneNumberControl.markAsTouched();
        }

        this.isPhoneValidationReady = true;
        this.updateMfaButtonState();
      });
    });

    const telephoneNumberControl = this.form.get('telephoneNumber');
    if (telephoneNumberControl) {
      merge(telephoneNumberControl.valueChanges, telephoneNumberControl.statusChanges)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateMfaButtonState();
        });
    }

    this.updateSuccess$.pipe(takeUntil(this.destroy$)).subscribe(({ updatedUser }) => {
      this.myself = { ...this.myself, ...updatedUser };
      this.userName = updatedUser?.name ?? this.userName;
      this.updateMfaButtonState();
    });

    this.form
      .get('languageId')
      ?.valueChanges.pipe(withLatestFrom(this.languages$), takeUntil(this.destroy$))
      .subscribe(([langId, languages]) => {
        const pickedLanguage = languages.find(langObj => langObj.id === langId);

        if (!pickedLanguage) {
          console.error('Language picked by user does not exist in languages$ collection');
          return;
        }

        this.translateService.use(pickedLanguage.key);
        this.ls.setUserLang(pickedLanguage);
        this.dateAdapter.setLocale(getLocaleForLang(pickedLanguage.key));
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public submitProfileForm(): void {
    const myRoleKey = this.myself?.role.key;

    const formValue = this.getSanitizedProfileFormValue();

    this.userName = formValue.name;

    // ADMINS
    if (myRoleKey === ROLES_KEYS.Admin) {
      this.form.markAsPristine();
      this.userAuthFacade.updateAdminself(this.myself?.id as string, formValue);
      return;
    }

    // EMPLOYEES
    if ([ROLES_KEYS.Employee, ROLES_KEYS.Team_Leader, ROLES_KEYS.Field_Consultant].includes(myRoleKey as ROLES_KEYS)) {
      delete formValue.email;
      delete formValue.officeId;
      this.submitOwnProfileUpdate(formValue as UpdateMyselfPayload);
      return;
    }

    // FOREIGNERS, MANAGERS and rest
    delete formValue.email;
    delete formValue.officeId;
    delete formValue.surname;
    this.submitOwnProfileUpdate(formValue as UpdateMyselfPayload);
  }

  public deleteAccount(): void {
    this.deleteAccountModalService.open();
  }

  public handleMfaAction(): void {
    if (this.isMfaButtonDisabled) {
      return;
    }

    this.enableMfaModalService
      .open({
        mode: this.myself?.mfaEnabled ? 'disable' : 'enable',
        phoneNumber: this.myself?.telephoneNumber,
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result?.success || typeof result?.mfaEnabled !== 'boolean') {
          return;
        }

        this.myself = { ...this.myself, mfaEnabled: result.mfaEnabled };
        this.updateMfaButtonState();
        this.userAuthFacade.getMyself(null);
      });
  }

  private updateMfaButtonState(): void {
    const telephoneNumberControl = this.form.get('telephoneNumber');
    const hasSavedPhoneNumber = !!this.myself?.telephoneNumber;

    this.isEnableMfaAction = this.myself?.mfaEnabled !== true;
    this.mfaButtonLabelKey = this.myself?.mfaEnabled
      ? 'ENABLE_MFA_MODAL.DISABLE_ACTION'
      : 'ENABLE_MFA_MODAL.ENABLE_ACTION';

    if (this.myself?.mfaEnabled === true) {
      this.isMfaButtonDisabled = false;
      return;
    }

    if (!this.isPhoneValidationReady) {
      this.isMfaButtonDisabled = true;
      return;
    }

    this.isMfaButtonDisabled =
      !hasSavedPhoneNumber || !!telephoneNumberControl?.invalid || !!telephoneNumberControl?.dirty;
  }

  public exitUserSettings(): void {
    this.routerFacade.goBack();
  }

  private getSanitizedProfileFormValue(): Record<string, any> {
    const formValue = { ...this.form.value };

    Object.keys(formValue).forEach(key => {
      if (!formValue[key]) {
        delete formValue[key];
      }
    });

    return formValue;
  }

  private submitOwnProfileUpdate(formValue: UpdateMyselfPayload): void {
    if (this.myself?.mfaEnabled) {
      this.openUpdateMySettingsMfaModal(formValue);
      return;
    }

    this.form.markAsPristine();
    this.userAuthFacade.updateMyself(formValue);
  }

  private openUpdateMySettingsMfaModal(formValue: UpdateMyselfPayload): void {
    this.mfaActionVerificationModalService
      .open({
        action: MfaChallengeAction.UPDATE_MY_SETTINGS,
        metadata: {
          payload: formValue,
        },
        phoneNumber: this.myself?.telephoneNumber ?? formValue.telephoneNumber ?? null,
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result?.success || result?.dismissed) {
          return;
        }

        const updatedUser = result.actionResult;

        if (updatedUser) {
          this.myself = { ...this.myself, ...updatedUser };
          this.userName = updatedUser.name ?? this.userName;
          this.form.patchValue({
            languageId: updatedUser.languageId ?? this.form.get('languageId')?.value,
            name: updatedUser.name ?? this.form.get('name')?.value,
            telephoneNumber: updatedUser.telephoneNumber ?? this.form.get('telephoneNumber')?.value,
          });
        }

        this.form.markAsPristine();
        this.updateMfaButtonState();
        this.userAuthFacade.getMyself(null);
      });
  }
}
